import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { EditorSection, Field } from '~/components/promotion/editor-section';
import type { PromotionFormValues } from '~/lib/schemas/promotion';

const MAX_DESCRIPTION = 500;

function ColorField({ name, label }: { name: 'colorHex' | 'backgroundColorHex'; label: string }) {
	const { control, setValue } = useFormContext<PromotionFormValues>();
	const value = useWatch({ control, name });
	return (
		<Field label={label} htmlFor={name}>
			<div className="flex items-center gap-2">
				<input
					type="color"
					value={value || '#000000'}
					onChange={e => setValue(name, e.target.value, { shouldDirty: true })}
					className="h-9 w-12 cursor-pointer rounded-md border border-border bg-surface p-1"
					aria-label={label}
				/>
				<Input
					id={name}
					value={value ?? ''}
					maxLength={10}
					onChange={e => setValue(name, e.target.value || null, { shouldDirty: true })}
					placeholder="#RRGGBB"
					className="max-w-40"
				/>
			</div>
		</Field>
	);
}

export function DescriptionSection() {
	const { t } = useTranslation();
	const { register, control } = useFormContext<PromotionFormValues>();
	const description = useWatch({ control, name: 'description' }) ?? '';

	return (
		<EditorSection title={t('promotion.basicInfo.description.title')} subtitle={t('promotion.basicInfo.description.subtitle')}>
			<div className="flex flex-col gap-4">
				<Field label={t('promotion.basicInfo.description.description')} htmlFor="description">
					<Textarea id="description" className="min-h-20" maxLength={MAX_DESCRIPTION} {...register('description')} />
					<span className="self-end text-xs text-foreground-subtle">
						{description.length} / {MAX_DESCRIPTION}
					</span>
				</Field>
				<Field
					label={t('promotion.basicInfo.description.badgeText')}
					help={t('promotion.basicInfo.description.badgeTextHelp')}
					htmlFor="badgeText">
					<Input id="badgeText" maxLength={20} {...register('badgeText')} />
				</Field>
				<Field
					label={t('promotion.basicInfo.description.frontLabel')}
					help={t('promotion.basicInfo.description.frontLabelHelp')}
					htmlFor="frontLabel">
					<Input id="frontLabel" maxLength={100} {...register('frontLabel')} />
				</Field>
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<ColorField name="colorHex" label={t('promotion.basicInfo.description.colorHex')} />
					<ColorField name="backgroundColorHex" label={t('promotion.basicInfo.description.backgroundColorHex')} />
				</div>
				<Field
					label={t('promotion.basicInfo.description.externalUrl')}
					help={t('promotion.basicInfo.description.externalUrlHelp')}
					htmlFor="externalUrl">
					<Input id="externalUrl" maxLength={2000} placeholder="https://… or /path" {...register('externalUrl')} />
				</Field>
			</div>
		</EditorSection>
	);
}
